import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { mlmAPI, getApiError } from '../../services/api';
import { Coins, Gift, Send, Info, ShoppingBag, Users, Star, TrendingUp } from 'lucide-react';

const SttoraPointsPage = () => {
  const { isDarkMode } = useTheme();
  const [points, setPoints] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState(null);

  useEffect(() => {
    Promise.all([
      mlmAPI.getSttoraPoints(),
      mlmAPI.getSttoraPointsTransactions(),
    ])
      .then(([ptsRes, txRes]) => {
        setPoints(ptsRes.data);
        setTransactions(txRes.data?.results || txRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRedeem = async () => {
    if (!redeemAmount || isNaN(redeemAmount) || Number(redeemAmount) <= 0) return;
    setRedeeming(true);
    setRedeemMsg(null);
    try {
      const res = await mlmAPI.redeemSttoraPoints({ points: Number(redeemAmount) });
      setRedeemMsg({ type: 'success', text: res.data?.message || 'Redemption successful!' });
      setRedeemAmount('');
      const ptsRes = await mlmAPI.getSttoraPoints();
      setPoints(ptsRes.data);
    } catch (err) {
      setRedeemMsg({ type: 'error', text: getApiError(err, 'Redemption failed') });
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;

  const earnMethods = [
    { icon: ShoppingBag, label: 'Store Purchases', desc: 'Earn points for every purchase in the STTORA store' },
    { icon: Users, label: 'Referrals', desc: 'Get bonus points when you refer new members' },
    { icon: Star, label: 'Rank Achievements', desc: 'Earn points when you reach new ranks' },
    { icon: TrendingUp, label: 'Volume Bonuses', desc: 'Points based on your network volume' },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Sttora Points</h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Your rewards and points management</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`${cardClass} bg-gradient-to-br from-orange-500 to-orange-600 text-white`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">Your Balance</h3>
            <Coins className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-4xl font-bold">{points?.balance?.toLocaleString() || '0'}</p>
          <p className="mt-1 text-orange-100">Available Points</p>
        </div>

        <div className={cardClass}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Total Earned</h3>
            <TrendingUp className="w-6 h-6 text-green-500" />
          </div>
          <p className={`text-4xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {points?.total_earned?.toLocaleString() || '0'}
          </p>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Points earned all time</p>
        </div>
      </div>

      <div className={cardClass}>
        <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Redeem Points</h3>
        {redeemMsg && (
          <div className={`mb-4 px-4 py-3 rounded-xl text-sm ${
            redeemMsg.type === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {redeemMsg.text}
          </div>
        )}
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Points to Redeem</label>
            <input
              type="number"
              value={redeemAmount}
              onChange={(e) => setRedeemAmount(e.target.value)}
              placeholder="Enter points"
              className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
          <button
            onClick={handleRedeem}
            disabled={redeeming}
            className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-medium hover:shadow-lg transition-shadow inline-flex items-center gap-2 disabled:opacity-50"
          >
            {redeeming ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Gift className="w-4 h-4" />
            )}
            Redeem
          </button>
        </div>
        <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Points value: 100 points = $1.00. Minimum redemption: 500 points.
        </p>
      </div>

      <div className={cardClass}>
        <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Transaction History</h3>
        {transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Points</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, i) => (
                  <tr key={tx.id || i} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                    <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`capitalize font-medium ${tx.type === 'earned' ? 'text-green-500' : 'text-red-500'}`}>
                        {tx.type}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-medium ${tx.type === 'earned' ? 'text-green-500' : 'text-red-500'}`}>
                        {tx.type === 'earned' ? '+' : '-'}{Math.abs(tx.points)?.toLocaleString()}
                      </span>
                    </td>
                    <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{tx.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No transactions yet</p>
        )}
      </div>

      <div className={cardClass}>
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-5 h-5 text-orange-500" />
          <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>How to Earn Points</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {earnMethods.map((method) => (
            <div key={method.label} className="flex items-start gap-3">
              <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <method.icon className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className={`font-medium text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{method.label}</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{method.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default SttoraPointsPage;
